// File: src/components/Procurement/POReceivingPanel.jsx

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useLocation,
} from "react-router-dom";

import api from "@/lib/axios";

import {
  Button,
} from "@/components/ui/button";

import {
  Input,
} from "@/components/ui/input";

import {
  Textarea,
} from "@/components/ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Label,
} from "@/components/ui/label";

import {
  toast,
} from "react-hot-toast";

import POTimelineLog from "./POTimelineLog";

import {
  useAuth,
} from "@/context/AuthProvider";

import {
  useUser as useUserCtx,
} from "@/context/UserContext";

import {
  useUserPermissions,
} from "@/hooks/useUserPermissions";

/* =============================================================================
   HELPERS
============================================================================= */

const toNum = (value) => {
  const n = Number(value);

  return Number.isFinite(n)
    ? n
    : 0;
};

const clamp = (
  value,
  min,
  max
) =>
  Math.max(
    min,
    Math.min(
      max,
      value
    )
  );

const legacyPoNo = (poId) =>
  `PO-${String(
    Number(poId) || 0
  ).padStart(
    5,
    "0"
  )}`;

const formatQty = (value) =>
  toNum(
    value
  ).toLocaleString(
    undefined,
    {
      maximumFractionDigits: 2,
    }
  );

/* =============================================================================
   EVIDENCE PICKER

   Important:
   The current backend stores photo evidence on a rejection/exception record.

   Therefore the controls are shown as Delivery / Exception Evidence and the
   file is submitted only when the line is part of an actual receiving action.
============================================================================= */

function EvidencePicker({
  file,
  disabled,
  onChange,
  onRemove,
}) {
  const cameraRef =
    useRef(null);

  const uploadRef =
    useRef(null);

  const [
    previewUrl,
    setPreviewUrl,
  ] =
    useState("");

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");

      return undefined;
    }

    const nextUrl =
      URL.createObjectURL(
        file
      );

    setPreviewUrl(
      nextUrl
    );

    return () => {
      URL.revokeObjectURL(
        nextUrl
      );
    };
  }, [
    file,
  ]);

  const chooseFile =
    (
      event
    ) => {
      const nextFile =
        event.target
          .files?.[0] ||
        null;

      if (
        nextFile
      ) {
        onChange(
          nextFile
        );
      }

      /*
       * Allow selecting the same image again later.
       */
      event.target.value =
        "";
    };

  return (
    <div className="rounded-2xl border border-violet-400/20 bg-violet-500/[0.05] p-4">
      <div className="mb-3">
        <div className="text-sm font-semibold text-violet-200">
          Delivery / Exception Evidence
        </div>

        <p className="mt-1 text-xs leading-5 text-slate-400">
          Add a photo when documenting damaged goods, quality issues,
          shortages, backorders or other receiving exceptions.
        </p>
      </div>

      <input
        ref={
          cameraRef
        }
        type="file"
        accept="image/*"
        capture="environment"
        disabled={
          disabled
        }
        onChange={
          chooseFile
        }
        className="hidden"
      />

      <input
        ref={
          uploadRef
        }
        type="file"
        accept="image/*"
        disabled={
          disabled
        }
        onChange={
          chooseFile
        }
        className="hidden"
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={
            disabled
          }
          onClick={() =>
            cameraRef.current?.click()
          }
          className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2.5 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-40"
        >
          📷 Take Photo
        </button>

        <button
          type="button"
          disabled={
            disabled
          }
          onClick={() =>
            uploadRef.current?.click()
          }
          className="rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-slate-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
        >
          🖼 Upload Photo
        </button>
      </div>

      {file ? (
        <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-black/20">
          {previewUrl ? (
            <img
              src={
                previewUrl
              }
              alt="Selected receiving evidence"
              className="max-h-60 w-full object-contain bg-black/30"
            />
          ) : null}

          <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-slate-200">
                {
                  file.name
                }
              </div>

              <div className="text-xs text-slate-500">
                {Math.max(
                  1,
                  Math.round(
                    file.size /
                      1024
                  )
                )}{" "}
                KB
              </div>
            </div>

            <button
              type="button"
              onClick={
                onRemove
              }
              className="rounded-lg border border-red-400/20 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-200 hover:bg-red-500/20"
            >
              Remove Photo
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 text-xs text-slate-500">
          No photo selected.
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   COMPONENT
============================================================================= */

const POReceivingPanel = ({
  poId,
}) => {
  const auth =
    useAuth?.() ||
    {};

  const userCtx =
    useUserCtx?.() ||
    {};

  const {
    role,
    hasPermission,
  } =
    useUserPermissions();

  const location =
    useLocation();

  /* ---------------------------------------------------------------------------
     Effective permissions
  --------------------------------------------------------------------------- */

  const canReceive =
    Boolean(
      hasPermission?.(
        "procurement.receiving.receive"
      )
    );

  const canReject =
    Boolean(
      hasPermission?.(
        "procurement.receiving.reject"
      )
    );

  const canBackorder =
    Boolean(
      hasPermission?.(
        "procurement.receiving.backorder"
      )
    );

  const canPerformAnyReceivingAction =
    Boolean(
      canReceive ||
      canReject ||
      canBackorder
    );

  const params =
    new URLSearchParams(
      location.search
    );

  const urlInspect =
    params.get(
      "mode"
    ) === "view" ||
    params.get(
      "log"
    ) === "1";

  const logRef =
    useRef(null);

  const user =
    userCtx.user ||
    auth.user ||
    {};

  const displayName =
    user.full_name ||
    user.name ||
    user.displayName ||
    user.email ||
    user.username ||
    (
      user.id
        ? `User #${user.id}`
        : "Unknown user"
    );

  const [
    poItems,
    setPoItems,
  ] =
    useState([]);

  const [
    poStatus,
    setPoStatus,
  ] =
    useState("");

  const [
    receivingLocked,
    setReceivingLocked,
  ] =
    useState(false);

  const [
    poNumber,
    setPoNumber,
  ] =
    useState("");

  const [
    formState,
    setFormState,
  ] =
    useState({});

  const [
    searchQuery,
    setSearchQuery,
  ] =
    useState("");

  const [
    itemsError,
    setItemsError,
  ] =
    useState(null);

  const [
    submitting,
    setSubmitting,
  ] =
    useState(false);

  /* =============================================================================
     API HELPERS
  ============================================================================= */

  const tryGet =
    async (
      paths
    ) => {
      let lastErr;

      for (
        const pathName
        of paths
      ) {
        try {
          const {
            data,
          } =
            await api.get(
              pathName
            );

          return data;
        } catch (
          error
        ) {
          lastErr =
            error;

          if (
            error
              ?.response
              ?.status !==
            404
          ) {
            throw error;
          }
        }
      }

      throw (
        lastErr ||
        new Error(
          "No endpoints matched"
        )
      );
    };

  /* =============================================================================
     PO DATA
  ============================================================================= */

  const fetchPoHeader =
    async () => {
      try {
        const {
          data,
        } =
          await api.get(
            `/purchase-orders/${poId}/summary`
          );

        const headerNo =
          data?.header
            ?.po_number ||
          data?.po_number ||
          data?.poNumber ||
          "";

        setPoNumber(
          headerNo
            ? String(
                headerNo
              )
            : ""
        );
      } catch {
        /*
         * Display enhancement only.
         */
        setPoNumber(
          ""
        );
      }
    };

  const fetchPoItems =
    async () => {
      setItemsError(
        null
      );

      try {
        const data =
          await tryGet([
            `/po/item-details/${poId}`,
            `/po/${poId}`,
            `/purchase-orders/${poId}/item-details`,
            `/purchase-orders/${poId}/items`,
            `/po/items/${poId}`,
          ]);

        if (
          data &&
          !Array.isArray(
            data
          ) &&
          Array.isArray(
            data.items
          )
        ) {
          setPoItems(
            data.items
          );

          setPoStatus(
            data.po_status ||
            ""
          );

          setReceivingLocked(
            Boolean(
              data.receiving_locked
            )
          );

          const pn =
            data.po_number ||
            data.poNumber;

          if (
            pn
          ) {
            setPoNumber(
              String(
                pn
              )
            );
          }

          return;
        }

        const rows =
          Array.isArray(
            data
          )
            ? data
            : Array.isArray(
                data?.items
              )
              ? data.items
              : [];

        setPoItems(
          rows
        );

        setPoStatus(
          ""
        );

        setReceivingLocked(
          false
        );
      } catch (
        error
      ) {
        console.error(
          "Failed to fetch PO items:",
          error
        );

        const status =
          error?.response
            ?.status;

        if (
          status ===
            401 ||
          status ===
            403
        ) {
          setItemsError(
            "You don't have permission to view this purchase order."
          );
        } else {
          setItemsError(
            "Unable to load purchase order items."
          );
        }

        setPoItems(
          []
        );

        setPoStatus(
          ""
        );

        setReceivingLocked(
          false
        );
      }
    };

  useEffect(() => {
    fetchPoHeader();
    fetchPoItems();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    poId,
  ]);

  useEffect(() => {
    if (
      urlInspect ||
      receivingLocked
    ) {
      const timer =
        setTimeout(
          () => {
            logRef.current?.scrollIntoView({
              behavior:
                "smooth",

              block:
                "start",
            });
          },
          0
        );

      return () =>
        clearTimeout(
          timer
        );
    }

    return undefined;
  }, [
    urlInspect,
    receivingLocked,
    poItems.length,
  ]);

  /* =============================================================================
     FORM STATE
  ============================================================================= */

  const setEntry =
    (
      poItemId,
      updater
    ) => {
      setFormState(
        (
          previous
        ) => {
          const previousEntry =
            previous[
              poItemId
            ] ||
            {};

          const next =
            typeof updater ===
            "function"
              ? updater(
                  previousEntry
                )
              : updater;

          return {
            ...previous,

            [poItemId]: {
              ...previousEntry,
              ...next,
            },
          };
        }
      );
    };

  const handleInputChange =
    (
      poItemId,
      field,
      value
    ) => {
      const item =
        poItems.find(
          (
            row
          ) =>
            row.po_item_id ===
            poItemId
        );

      const remaining =
        toNum(
          item
            ?.remaining_quantity
        );

      if (
        field ===
        "received_quantity"
      ) {
        if (
          !canReceive
        ) {
          return;
        }

        const raw =
          toNum(
            value
          );

        const clamped =
          clamp(
            raw,
            0,
            Math.max(
              0,
              remaining
            )
          );

        setEntry(
          poItemId,
          {
            received_quantity:
              clamped,
          }
        );

        return;
      }

      if (
        field ===
        "rejection_reason"
      ) {
        if (
          !canReject
        ) {
          return;
        }
      }

      if (
        field ===
        "rejection_action"
      ) {
        if (
          value ===
            "Reorder" &&
          !canBackorder
        ) {
          return;
        }

        if (
          (
            value ===
              "Credit" ||
            value ===
              "Ignore"
          ) &&
          !canReject
        ) {
          return;
        }
      }

      setEntry(
        poItemId,
        {
          [field]:
            value,
        }
      );
    };

  const toggleReject =
    (
      poItemId,
      checked
    ) => {
      if (
        !canReject
      ) {
        return;
      }

      setEntry(
        poItemId,
        (
          previous
        ) => ({
          rejection_enabled:
            checked,

          rejection_reason:
            checked
              ? previous.rejection_reason ||
                ""
              : "",

          rejection_action:
            checked
              ? previous.rejection_action ||
                ""
              : "",

          photo:
            checked
              ? previous.photo ||
                null
              : null,
        })
      );
    };

  const toggleBackorder =
    (
      poItemId,
      checked
    ) => {
      if (
        !canBackorder
      ) {
        return;
      }

      setEntry(
        poItemId,
        (
          previous
        ) => ({
          rejection_enabled:
            checked
              ? true
              : previous.rejection_enabled,

          rejection_action:
            checked
              ? "Reorder"
              : previous.rejection_action ===
                  "Reorder"
                ? ""
                : previous.rejection_action,
        })
      );
    };

  const toggleIgnore =
    (
      poItemId,
      checked
    ) => {
      if (
        !canReject
      ) {
        return;
      }

      setEntry(
        poItemId,
        (
          previous
        ) => ({
          rejection_enabled:
            checked
              ? true
              : previous.rejection_enabled,

          rejection_action:
            checked
              ? "Ignore"
              : previous.rejection_action ===
                  "Ignore"
                ? ""
                : previous.rejection_action,

          /*
           * Ignore does not need rejection evidence.
           */
          photo:
            checked
              ? null
              : previous.photo,
        })
      );
    };

  const removePhoto =
    (
      poItemId
    ) => {
      setEntry(
        poItemId,
        {
          photo:
            null,
        }
      );
    };

  /* =============================================================================
     PAYLOAD
  ============================================================================= */

  const buildPayloadItems =
    () =>
      poItems
        .map(
          (
            item
          ) => {
            const entry =
              formState[
                item.po_item_id
              ] ||
              {};

            const remaining =
              toNum(
                item
                  .remaining_quantity
              );

            const receivedQty =
              clamp(
                toNum(
                  entry.received_quantity
                ),
                0,
                Math.max(
                  0,
                  remaining
                )
              );

            const remainder =
              Math.max(
                remaining -
                  receivedQty,
                0
              );

            const action =
              String(
                entry.rejection_action ||
                ""
              );

            const hasException =
              Boolean(
                entry.rejection_enabled
              ) ||
              action ===
                "Reorder" ||
              action ===
                "Credit" ||
              action ===
                "Ignore";

            const payload = {
              po_item_id:
                item.po_item_id,

              received_quantity:
                receivedQty,

              temperature:
                entry.temperature ??
                "",
            };

            if (
              hasException &&
              remainder >
                0
            ) {
              if (
                action ===
                "Reorder"
              ) {
                payload.rejection_action =
                  "Reorder";

                payload.is_backordered =
                  true;

                /*
                 * Backend will use the remaining quantity for a backorder
                 * if quantity_rejected is omitted.
                 */
              } else if (
                action ===
                "Ignore"
              ) {
                payload.rejection_action =
                  "Ignore";

                payload.is_ignore =
                  true;
              } else if (
                action ===
                  "Credit" ||
                entry.rejection_enabled
              ) {
                payload.quantity_rejected =
                  remainder;

                payload.is_rejected =
                  true;

                if (
                  action
                ) {
                  payload.rejection_action =
                    action;
                }

                if (
                  String(
                    entry.rejection_reason ||
                    ""
                  ).trim()
                ) {
                  payload.rejection_reason =
                    String(
                      entry.rejection_reason
                    ).trim();
                }
              }
            }

            if (
              entry.photo
            ) {
              payload._photo =
                entry.photo;
            }

            return payload;
          }
        )
        .filter(
          (
            item
          ) =>
            toNum(
              item.received_quantity
            ) >
              0 ||
            toNum(
              item.quantity_rejected
            ) >
              0 ||
            item.rejection_action ===
              "Reorder" ||
            item.rejection_action ===
              "Credit" ||
            item.rejection_action ===
              "Ignore" ||
            item.is_backordered ===
              true ||
            item.is_ignore ===
              true
        );

  const requiredPermissionsForItems =
    (
      items
    ) => {
      let needsReceive =
        false;

      let needsReject =
        false;

      let needsBackorder =
        false;

      for (
        const item
        of items
      ) {
        if (
          toNum(
            item.received_quantity
          ) >
          0
        ) {
          needsReceive =
            true;
        }

        if (
          item.rejection_action ===
            "Reorder" ||
          item.is_backordered ===
            true
        ) {
          needsBackorder =
            true;
        }

        if (
          toNum(
            item.quantity_rejected
          ) >
            0 ||
          item.rejection_action ===
            "Credit" ||
          item.rejection_action ===
            "Ignore" ||
          item.is_ignore ===
            true
        ) {
          needsReject =
            true;
        }
      }

      return {
        needsReceive,
        needsReject,
        needsBackorder,
      };
    };

  /* =============================================================================
     SUBMIT
  ============================================================================= */

  const handleSubmit =
    async () => {
      if (
        receivingLocked
      ) {
        toast.error(
          "Receiving is blocked until this PO reaches an approved receiving status."
        );

        return;
      }

      const items =
        buildPayloadItems();

      if (
        !items.length
      ) {
        toast.error(
          "Nothing to submit. Enter a received quantity or select an exception action."
        );

        return;
      }

      /*
       * Validate exception selections before sending.
       */
      for (
        const payloadItem
        of items
      ) {
        const entry =
          formState[
            payloadItem.po_item_id
          ] ||
          {};

        const remaining =
          toNum(
            poItems.find(
              (
                row
              ) =>
                row.po_item_id ===
                payloadItem.po_item_id
            )?.remaining_quantity
          );

        const receivedQty =
          toNum(
            payloadItem.received_quantity
          );

        const remainder =
          Math.max(
            remaining -
              receivedQty,
            0
          );

        if (
          entry.rejection_enabled &&
          remainder >
            0 &&
          !entry.rejection_action
        ) {
          toast.error(
            "Select an exception action for each rejected item."
          );

          return;
        }

        if (
          entry.rejection_enabled &&
          entry.rejection_action !==
            "Ignore" &&
          !String(
            entry.rejection_reason ||
            ""
          ).trim()
        ) {
          toast.error(
            "Enter a reason for each rejection/backorder/credit."
          );

          return;
        }
      }

      const {
        needsReceive,
        needsReject,
        needsBackorder,
      } =
        requiredPermissionsForItems(
          items
        );

      if (
        needsReceive &&
        !canReceive
      ) {
        toast.error(
          "You do not have permission to receive PO quantities."
        );

        return;
      }

      if (
        needsReject &&
        !canReject
      ) {
        toast.error(
          "You do not have permission to reject, credit or ignore PO quantities."
        );

        return;
      }

      if (
        needsBackorder &&
        !canBackorder
      ) {
        toast.error(
          "You do not have permission to backorder PO quantities."
        );

        return;
      }

      const formData =
        new FormData();

      formData.append(
        "po_id",
        poId
      );

      formData.append(
        "items",
        JSON.stringify(
          items.map(
            ({
              _photo,
              ...rest
            }) =>
              rest
          )
        )
      );

      /*
       * Keep the current backend contract:
       *
       * photo_<po_item_id>
       */
      items.forEach(
        (
          row
        ) => {
          if (
            row._photo
          ) {
            formData.append(
              `photo_${row.po_item_id}`,
              row._photo
            );
          }
        }
      );

      try {
        setSubmitting(
          true
        );

        const {
          data,
        } =
          await api.post(
            "/po/receive",
            formData
          );

        toast.success(
          "PO receiving submitted."
        );

        if (
          Array.isArray(
            data?.rejectedItems
          ) &&
          data.rejectedItems
            .length
        ) {
          toast(
            `${data.rejectedItems.length} item(s) recorded as receiving exceptions.`,
            {
              icon:
                "ℹ️",
            }
          );
        }

        if (
          data?.poStatus
        ) {
          toast(
            `PO status: ${data.poStatus}`,
            {
              icon:
                "📦",
            }
          );
        }

        if (
          data?.autoInvoiceId
        ) {
          toast.success(
            `Auto-invoice #${data.autoInvoiceId} synchronized.`
          );
        }

        setFormState(
          {}
        );

        await Promise.all([
          fetchPoHeader(),
          fetchPoItems(),
        ]);
      } catch (
        error
      ) {
        const response =
          error?.response
            ?.data;

        const message =
          response?.details ||
          response?.error ||
          response?.message ||
          "Error submitting PO receiving.";

        toast.error(
          String(
            message
          )
        );

        console.error(
          "PO receiving submit failed:",
          error
        );
      } finally {
        setSubmitting(
          false
        );
      }
    };

  /* =============================================================================
     DISPLAY
  ============================================================================= */

  const filteredItems =
    useMemo(
      () =>
        poItems.filter(
          (
            item
          ) =>
            [
              item.item_code,
              item.item_name,
              item.description,
            ]
              .filter(
                Boolean
              )
              .some(
                (
                  value
                ) =>
                  String(
                    value
                  )
                    .toLowerCase()
                    .includes(
                      searchQuery
                        .trim()
                        .toLowerCase()
                    )
              )
        ),
      [
        poItems,
        searchQuery,
      ]
    );

  const displayPoNo =
    poNumber ||
    legacyPoNo(
      poId
    );

  return (
    <div className="space-y-6">
      {/* =====================================================================
          PO operational header
      ===================================================================== */}

      <section className="rounded-3xl border border-white/10 bg-[#0d1329]/90 p-5 shadow-xl sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300/70">
              Receiving Workspace
            </div>

            <h2 className="mt-1 text-xl font-bold text-white sm:text-2xl">
              {
                displayPoNo
              }
            </h2>

            {poStatus ? (
              <div className="mt-2 inline-flex rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300">
                {
                  poStatus
                }
              </div>
            ) : null}
          </div>

          <div className="rounded-2xl border border-cyan-400/15 bg-cyan-500/[0.06] px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide text-cyan-300/70">
              Receiving as
            </div>

            <div className="mt-1 font-semibold text-cyan-100">
              {
                displayName
              }
            </div>

            {role ? (
              <div className="mt-0.5 text-xs text-slate-400">
                {
                  role
                }
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* =====================================================================
          LOCK / VIEW ONLY
      ===================================================================== */}

      {receivingLocked ? (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-5 text-amber-100">
          <div className="font-semibold">
            Receiving is blocked
          </div>

          <p className="mt-1 text-sm leading-6 text-amber-100/80">
            This PO is{" "}
            <span className="font-semibold">
              {poStatus ||
                "not yet approved for receiving"}
            </span>
            . The purchase order and activity log remain available for inspection.
          </p>

          <button
            type="button"
            onClick={() =>
              logRef.current?.scrollIntoView({
                behavior:
                  "smooth",
                block:
                  "start",
              })
            }
            className="mt-3 text-sm font-semibold underline"
          >
            View PO Activity →
          </button>
        </div>
      ) : null}

      {!receivingLocked &&
      !canPerformAnyReceivingAction ? (
        <div className="rounded-2xl border border-slate-500/30 bg-slate-500/10 p-5 text-slate-200">
          <div className="font-semibold">
            View only
          </div>

          <p className="mt-1 text-sm text-slate-400">
            You can inspect this PO but do not currently have permission to perform receiving actions.
          </p>
        </div>
      ) : null}

      {itemsError ? (
        <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-5 text-red-200">
          {
            itemsError
          }
        </div>
      ) : null}

      {/* =====================================================================
          SEARCH
      ===================================================================== */}

      <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
        <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-400">
          Find Item
        </Label>

        <Input
          type="text"
          placeholder="Search by item code or item name..."
          value={
            searchQuery
          }
          onChange={
            (
              event
            ) =>
              setSearchQuery(
                event.target
                  .value
              )
          }
          className="border-white/10 bg-black/20 text-white placeholder:text-slate-500"
        />
      </div>

      {/* =====================================================================
          ITEMS
      ===================================================================== */}

      <div className="space-y-4">
        {filteredItems.map(
          (
            item
          ) => {
            const entry =
              formState[
                item.po_item_id
              ] ||
              {};

            const remaining =
              toNum(
                item.remaining_quantity
              );

            const ordered =
              toNum(
                item.ordered_quantity ??
                item.quantity
              );

            const lifetimeReceived =
              toNum(
                item.quantity_received ??
                item.received_quantity
              );

            const lifetimeIgnored =
              toNum(
                item.quantity_ignored ??
                item.ignored_quantity
              );

            const receivedNow =
              toNum(
                entry.received_quantity
              );

            const remainder =
              Math.max(
                remaining -
                  receivedNow,
                0
              );

            const action =
              entry.rejection_action ||
              "";

            const isBackorder =
              action ===
              "Reorder";

            const isIgnore =
              action ===
              "Ignore";

            const isCredit =
              action ===
              "Credit";

            const exceptionEnabled =
              Boolean(
                entry.rejection_enabled
              ) ||
              isBackorder ||
              isIgnore ||
              isCredit;

            const disabledByQty =
              remaining <=
              0;

            const receiveDisabled =
              receivingLocked ||
              disabledByQty ||
              !canReceive;

            const rejectDisabled =
              receivingLocked ||
              disabledByQty ||
              !canReject;

            const backorderDisabled =
              receivingLocked ||
              disabledByQty ||
              !canBackorder;

            const evidenceDisabled =
              receivingLocked ||
              disabledByQty ||
              !canReject;

            return (
              <article
                key={
                  item.po_item_id
                }
                className={`overflow-hidden rounded-3xl border ${
                  disabledByQty
                    ? "border-white/5 bg-white/[0.025] opacity-60"
                    : "border-white/10 bg-[#0d1329]/85"
                }`}
              >
                {/* -------------------------------------------------------------
                    ITEM HEADER
                ------------------------------------------------------------- */}

                <div className="border-b border-white/10 p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-cyan-300/70">
                        {
                          item.item_code ||
                          "Item"
                        }
                      </div>

                      <h3 className="mt-1 text-lg font-bold text-white">
                        {item.item_name ||
                          item.description ||
                          "Unnamed item"}
                      </h3>

                      {item.unit_size ? (
                        <div className="mt-1 text-xs text-slate-500">
                          Unit:{" "}
                          {
                            item.unit_size
                          }
                        </div>
                      ) : null}
                    </div>

                    {disabledByQty ? (
                      <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-200">
                        Complete
                      </div>
                    ) : null}
                  </div>

                  {/* -----------------------------------------------------------
                      QUANTITY SUMMARY
                  ----------------------------------------------------------- */}

                  <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        Ordered
                      </div>

                      <div className="mt-1 text-lg font-bold text-white">
                        {
                          formatQty(
                            ordered
                          )
                        }
                      </div>
                    </div>

                    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        Received
                      </div>

                      <div className="mt-1 text-lg font-bold text-emerald-300">
                        {
                          formatQty(
                            lifetimeReceived
                          )
                        }
                      </div>
                    </div>

                    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        Ignored
                      </div>

                      <div className="mt-1 text-lg font-bold text-slate-300">
                        {
                          formatQty(
                            lifetimeIgnored
                          )
                        }
                      </div>
                    </div>

                    <div className="rounded-xl border border-amber-400/20 bg-amber-500/[0.07] p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-300/70">
                        Remaining
                      </div>

                      <div className="mt-1 text-lg font-bold text-amber-200">
                        {
                          formatQty(
                            remaining
                          )
                        }
                      </div>
                    </div>
                  </div>
                </div>

                {!disabledByQty ? (
                  <div className="space-y-6 p-5">
                    {/* ---------------------------------------------------------
                        RECEIVE
                    --------------------------------------------------------- */}

                    {canReceive ? (
                      <section>
                        <div className="mb-3">
                          <h4 className="font-semibold text-white">
                            Receive This Delivery
                          </h4>

                          <p className="mt-1 text-xs text-slate-400">
                            Enter only what has physically arrived during this receiving stage.
                          </p>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <Label className="mb-2 block text-sm text-slate-300">
                              Quantity Received
                            </Label>

                            <Input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="any"
                              max={
                                Math.max(
                                  0,
                                  remaining
                                )
                              }
                              disabled={
                                receiveDisabled
                              }
                              value={
                                entry.received_quantity ??
                                ""
                              }
                              onChange={
                                (
                                  event
                                ) =>
                                  handleInputChange(
                                    item.po_item_id,
                                    "received_quantity",
                                    event.target
                                      .value
                                  )
                              }
                              className="border-white/10 bg-black/20 text-white"
                            />

                            <div className="mt-2 text-xs text-slate-500">
                              Maximum available:{" "}
                              {
                                formatQty(
                                  remaining
                                )
                              }
                            </div>
                          </div>

                          <div>
                            <Label className="mb-2 block text-sm text-slate-300">
                              Temperature °C
                            </Label>

                            <Input
                              type="number"
                              inputMode="decimal"
                              step="0.1"
                              min="-100"
                              max="100"
                              disabled={
                                receiveDisabled
                              }
                              value={
                                entry.temperature ??
                                ""
                              }
                              placeholder="e.g. 3.4"
                              onChange={
                                (
                                  event
                                ) =>
                                  handleInputChange(
                                    item.po_item_id,
                                    "temperature",
                                    event.target
                                      .value
                                  )
                              }
                              className="border-white/10 bg-black/20 text-white"
                            />

                            <div className="mt-2 text-xs text-slate-500">
                              Record when temperature control is required.
                            </div>
                          </div>
                        </div>

                        {receivedNow >
                        0 ? (
                          <div className="mt-4 rounded-xl border border-emerald-400/20 bg-emerald-500/[0.07] p-3 text-sm text-emerald-200">
                            Receiving now:{" "}
                            <span className="font-bold">
                              {
                                formatQty(
                                  receivedNow
                                )
                              }
                            </span>

                            {remainder >
                            0 ? (
                              <>
                                {" "}
                                · Remaining after this receipt:{" "}
                                <span className="font-bold">
                                  {
                                    formatQty(
                                      remainder
                                    )
                                  }
                                </span>
                              </>
                            ) : (
                              <>
                                {" "}
                                · This line will be fully received.
                              </>
                            )}
                          </div>
                        ) : null}
                      </section>
                    ) : null}

                    {/* ---------------------------------------------------------
                        EXCEPTION
                    --------------------------------------------------------- */}

                    {(canReject ||
                      canBackorder) &&
                    remainder >
                      0 ? (
                      <section className="rounded-2xl border border-amber-400/15 bg-amber-500/[0.04] p-4">
                        <div className="mb-4">
                          <h4 className="font-semibold text-white">
                            Receiving Exception
                          </h4>

                          <p className="mt-1 text-xs leading-5 text-slate-400">
                            Use this only when some of the remaining quantity is not accepted as a normal receipt.
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-3">
                          {canReject ? (
                            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-200">
                              <input
                                type="checkbox"
                                disabled={
                                  rejectDisabled
                                }
                                checked={
                                  Boolean(
                                    entry.rejection_enabled
                                  ) &&
                                  !isBackorder &&
                                  !isIgnore
                                }
                                onChange={
                                  (
                                    event
                                  ) =>
                                    toggleReject(
                                      item.po_item_id,
                                      event.target
                                        .checked
                                    )
                                }
                              />

                              Reject / Credit
                            </label>
                          ) : null}

                          {canBackorder ? (
                            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-200">
                              <input
                                type="checkbox"
                                disabled={
                                  backorderDisabled
                                }
                                checked={
                                  isBackorder
                                }
                                onChange={
                                  (
                                    event
                                  ) =>
                                    toggleBackorder(
                                      item.po_item_id,
                                      event.target
                                        .checked
                                    )
                                }
                              />

                              Backorder
                            </label>
                          ) : null}

                          {canReject ? (
                            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-slate-200">
                              <input
                                type="checkbox"
                                disabled={
                                  rejectDisabled
                                }
                                checked={
                                  isIgnore
                                }
                                onChange={
                                  (
                                    event
                                  ) =>
                                    toggleIgnore(
                                      item.po_item_id,
                                      event.target
                                        .checked
                                    )
                                }
                              />

                              Ignore Remainder
                            </label>
                          ) : null}
                        </div>

                        {exceptionEnabled ? (
                          <div className="mt-5 space-y-4">
                            <div>
                              <Label className="mb-2 block text-sm text-slate-300">
                                Exception Action
                              </Label>

                              <Select
                                disabled={
                                  receivingLocked
                                }
                                value={
                                  action ||
                                  undefined
                                }
                                onValueChange={
                                  (
                                    value
                                  ) =>
                                    handleInputChange(
                                      item.po_item_id,
                                      "rejection_action",
                                      value
                                    )
                                }
                              >
                                <SelectTrigger className="border-white/10 bg-black/20 text-white">
                                  <SelectValue placeholder="Select action" />
                                </SelectTrigger>

                                <SelectContent>
                                  {canBackorder ? (
                                    <SelectItem value="Reorder">
                                      Reorder / Backorder
                                    </SelectItem>
                                  ) : null}

                                  {canReject ? (
                                    <SelectItem value="Credit">
                                      Credit
                                    </SelectItem>
                                  ) : null}

                                  {canReject ? (
                                    <SelectItem value="Ignore">
                                      Ignore
                                    </SelectItem>
                                  ) : null}
                                </SelectContent>
                              </Select>
                            </div>

                            {!isIgnore ? (
                              <div>
                                <Label className="mb-2 block text-sm text-slate-300">
                                  Reason
                                </Label>

                                <Textarea
                                  disabled={
                                    isBackorder
                                      ? backorderDisabled
                                      : rejectDisabled
                                  }
                                  value={
                                    entry.rejection_reason ||
                                    ""
                                  }
                                  placeholder="Describe the issue: damaged packaging, short delivery, incorrect item, quality concern..."
                                  onChange={
                                    (
                                      event
                                    ) =>
                                      handleInputChange(
                                        item.po_item_id,
                                        "rejection_reason",
                                        event.target
                                          .value
                                      )
                                  }
                                  className="min-h-24 border-white/10 bg-black/20 text-white placeholder:text-slate-600"
                                />
                              </div>
                            ) : (
                              <div className="rounded-xl border border-slate-400/15 bg-slate-500/[0.06] p-3 text-xs text-slate-400">
                                Ignore removes this remainder from the outstanding PO balance. No automatic Ignore is selected — it must be chosen deliberately.
                              </div>
                            )}

                            {!isIgnore ? (
                              <EvidencePicker
                                file={
                                  entry.photo ||
                                  null
                                }
                                disabled={
                                  evidenceDisabled
                                }
                                onChange={
                                  (
                                    file
                                  ) =>
                                    handleInputChange(
                                      item.po_item_id,
                                      "photo",
                                      file
                                    )
                                }
                                onRemove={() =>
                                  removePhoto(
                                    item.po_item_id
                                  )
                                }
                              />
                            ) : null}

                            <div className="rounded-xl border border-white/10 bg-black/20 p-3 text-sm">
                              <span className="text-slate-400">
                                Exception quantity this stage:
                              </span>{" "}
                              <span className="font-bold text-amber-200">
                                {
                                  formatQty(
                                    remainder
                                  )
                                }
                              </span>
                            </div>
                          </div>
                        ) : null}
                      </section>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          }
        )}

        {!filteredItems.length &&
        !itemsError ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-slate-400">
            No PO items match your search.
          </div>
        ) : null}
      </div>

      {/* =====================================================================
          SUBMIT
      ===================================================================== */}

      {canPerformAnyReceivingAction ? (
        <div className="sticky bottom-4 z-20 rounded-2xl border border-cyan-400/20 bg-[#080d1f]/95 p-4 shadow-2xl backdrop-blur">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-white">
                Submit Receiving
              </div>

              <div className="mt-0.5 text-xs text-slate-400">
                Only entered receipt quantities and selected exception actions will be recorded.
              </div>
            </div>

            <Button
              type="button"
              onClick={
                handleSubmit
              }
              disabled={
                submitting ||
                receivingLocked
              }
              className="min-w-44 bg-cyan-500 font-semibold text-black hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting
                ? "Submitting..."
                : "Submit Receiving"}
            </Button>
          </div>
        </div>
      ) : null}

      {/* =====================================================================
          PO TIMELINE
      ===================================================================== */}

      <div
        id="po-log"
        ref={
          logRef
        }
        className="pt-4"
      >
        <POTimelineLog
          poId={
            poId
          }
        />
      </div>
    </div>
  );
};

export default POReceivingPanel;